import { useMemo } from 'react';
import { useAlerts, useCrashes, useDevices, useVehicles } from '@/features/shared/queries';
import type { Alert, CrashEvent, Device, Vehicle } from '@/types/api';

export interface CrashDetail {
  crash: CrashEvent;
  device: Device | null;
  vehicle: Vehicle | null;
  alerts: Alert[];
  /** True while any alert for this crash is still being attempted. */
  dispatchPending: boolean;
  failedCount: number;
}

/** How far back the crash list is searched when resolving a deep link. */
const LOOKBACK = 500;

/**
 * Assembles everything about one crash.
 *
 * The API has no per-crash endpoint and its crash payload carries no vehicle or
 * owner, so this joins four separate reads: the crash out of the recent-crashes
 * window, its device by id, that device's vehicle, and the alerts filtered by
 * crash_event_id. TanStack Query dedupes the underlying requests, so the pages that
 * already show these lists pay nothing extra.
 *
 * A crash older than the lookback window genuinely cannot be resolved — the caller
 * gets `notFound` and says so, rather than rendering a page of blanks.
 */
export function useCrashDetail(crashId: number) {
  const crashes = useCrashes(LOOKBACK);
  const devices = useDevices();
  const vehicles = useVehicles();
  const alerts = useAlerts(500);

  const detail = useMemo<CrashDetail | null>(() => {
    const crash = crashes.data?.find((entry) => entry.id === crashId);
    if (!crash) return null;

    const device = devices.data?.find((entry) => entry.id === crash.device_id) ?? null;
    const vehicle =
      device?.vehicle_id != null
        ? (vehicles.data?.find((entry) => entry.id === device.vehicle_id) ?? null)
        : null;

    const related = (alerts.data ?? []).filter((alert) => alert.crash_event_id === crashId);

    return {
      crash,
      device,
      vehicle,
      alerts: related,
      dispatchPending: related.some((alert) => alert.status === 'pending'),
      failedCount: related.filter((alert) => alert.status === 'failed').length,
    };
  }, [crashId, crashes.data, devices.data, vehicles.data, alerts.data]);

  // Devices and vehicles are supporting detail; the page is useful without them,
  // and a responder cannot read them at all. Only the crash list is load-bearing.
  const isPending = crashes.isPending || alerts.isPending;

  return {
    detail,
    isPending,
    isError: crashes.isError,
    error: crashes.error,
    notFound: !isPending && !crashes.isError && detail === null,
    refetch: () => {
      void crashes.refetch();
      void alerts.refetch();
    },
  };
}
